import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ConfirmProvider } from './context/ConfirmContext';
import PrivateRoute from './components/PrivateRoute';
import AppLayout from './components/AppLayout';
import CookieConsentBanner from './components/CookieConsentBanner';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Companies from './pages/Companies';
import MyCompany from './pages/MyCompany';
import Users from './pages/Users';
import UserEdit from './pages/UserEdit';
import Tasks from './pages/Tasks';
import TaskDetail from './pages/TaskDetail';
import TaskLog from './pages/TaskLog';
import TimeReport from './pages/TimeReport';
import WorkHourRules from './pages/WorkHourRules';
import Locations from './pages/Locations';
import Customers from './pages/Customers';
import Articles from './pages/Articles';
import PresetChecklists from './pages/PresetChecklists';
import TaskPresets from './pages/TaskPresets';
import Handouts from './pages/Handouts';
import WorkGroups from './pages/WorkGroups';
import Analytics from './pages/Analytics';
import PlanGatedRoute from './components/PlanGatedRoute';
import NoCompany from './pages/NoCompany';
import Onboarding from './pages/Onboarding';
import AcceptInvitation from './pages/AcceptInvitation';
import SuperAdminView from './pages/SuperAdminView';
import RequestSupport from './pages/RequestSupport';
import NotFound from './pages/NotFound';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import usePageAnalytics from './hooks/usePageAnalytics';
import './App.css';

const PageAnalytics = () => {
  usePageAnalytics();
  return null;
};

function App() {
  return (
    <AuthProvider>
      <ConfirmProvider>
        <Router>
          <div className="App">
            <PageAnalytics />
            <CookieConsentBanner />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/cookies" element={<CookiePolicy />} />

              <Route element={<PrivateRoute><AppLayout /></PrivateRoute>}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/users/edit" element={<UserEdit />} />
                <Route path="/support" element={<RequestSupport />} />
                <Route path="/tasks" element={<Tasks />} />
                <Route path="/tasks/:id" element={<TaskDetail />} />
                <Route path="/task-export" element={<TaskLog />} />
                <Route path="/task-log" element={<Navigate to="/task-export" replace />} />
                <Route path="/time-report" element={<TimeReport />} />
                <Route path="/handouts" element={<PlanGatedRoute capability="handouts"><Handouts /></PlanGatedRoute>} />
              </Route>

              <Route element={<PrivateRoute><AppLayout /></PrivateRoute>}>
                <Route path="/no-company" element={<NoCompany />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/accept-invitation" element={<AcceptInvitation />} />
              </Route>

              <Route element={<PrivateRoute allowedRoles={['super_admin']}><AppLayout /></PrivateRoute>}>
                <Route path="/super-admin" element={<SuperAdminView />} />
                <Route path="/companies" element={<Companies />} />
              </Route>

              <Route element={<PrivateRoute allowedRoles={['admin']}><AppLayout /></PrivateRoute>}>
                <Route path="/my-company" element={<MyCompany />} />
                <Route path="/work-hour-rules" element={<WorkHourRules />} />
              </Route>

              <Route element={<PrivateRoute allowedRoles={['super_admin', 'admin']}><AppLayout /></PrivateRoute>}>
                <Route path="/users" element={<Users />} />
                <Route path="/roles" element={<Navigate to="/dashboard" replace />} />
                <Route path="/locations" element={<Locations />} />
                <Route path="/customers" element={<Customers />} />
                <Route path="/articles" element={<Articles />} />
                <Route path="/work-groups" element={<PlanGatedRoute capability="taskWorkGroups"><WorkGroups /></PlanGatedRoute>} />
                <Route path="/preset-checklists" element={<PlanGatedRoute capability="checklistPresets"><PresetChecklists /></PlanGatedRoute>} />
                <Route path="/task-presets" element={<PlanGatedRoute capability="taskPresets"><TaskPresets /></PlanGatedRoute>} />
                <Route path="/export" element={<Navigate to="/task-export" replace />} />
                <Route path="/analytics" element={<PlanGatedRoute capability="advancedAnalytics"><Analytics /></PlanGatedRoute>} />
              </Route>

              <Route element={<PrivateRoute><AppLayout /></PrivateRoute>}>
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </div>
        </Router>
      </ConfirmProvider>
    </AuthProvider>
  );
}

export default App;
